import React from 'react';
import type { EditorBlock } from '@/store/editorSlice';
import { primaryButtonStyle } from '@/themes';
import type { ButtonStyle } from '@/themes/theme.types';
import { getPublicQuizStrings } from '@/utils/publicQuizStrings';
import type { ResolvedScreenLayout } from '@/components/screen-layout/resolveScreenLayout';
import { screenAdvanceIsAutoOnly, screenHasAdvanceMeans } from './screenAdvance';
import { keepFieldFocusOnPress } from './keepFieldFocusOnPress';
import type { QuizChromeKit } from './quizChromeKitDoor';
import type { QuizChromeExtras } from './useQuizChromeExtras';

/**
 * Системная запасная кнопка «Далее» плеера квиза (не блок) — либо `null`.
 *
 * Функция отрисовки, а не компонент: плеер кладёт её результат в экран как
 * раньше клал ту же разметку — дерево узлов не меняется.
 *
 * WEB-3402: вынесено из QuizScreenWizard.tsx (ветка ctaNode) без изменений
 * поведения. Сторож паритета WEB-1704 (QuizScreenWizard.advanceParity) и тесты
 * запасной кнопки по-прежнему судят её через сам плеер.
 *
 * Параметры позиционные, а не объектом: имена ключей объекта минификатор не
 * сжимает, а запас веса первого экрана покупателя — десятки байт.
 *
 * @param visibleBlocks блоки экрана без скрытых правилом видимости
 * @param rescueVisible авто-переход на этом экране упёрся и истёк таймер
 *   показа (useAutoAdvance)
 * @param screenLayout раскладка экрана (resolveScreenLayout)
 * @param dimmed на экране есть незаполненные обязательные блоки (WEB-922)
 * @param packNextButtonStyle вид кнопки «Далее» из пака (или undefined)
 */
export function renderQuizFallbackCta(
  visibleBlocks: EditorBlock[],
  rescueVisible: boolean,
  screenLayout: ResolvedScreenLayout,
  isLast: boolean,
  dimmed: boolean,
  packNextButtonStyle: React.CSSProperties | undefined,
  onNext: () => void,
  chromeExtras: QuizChromeExtras,
  chromeKit: QuizChromeKit,
  isEditorPreview: boolean,
  buttonStyle: ButtonStyle | undefined,
  textColor: string | undefined,
  language: string | undefined,
): React.ReactElement | null {
  // WEB-1704: три признака «есть чем продвинуться» (блок «Кнопка Далее» —
  // QA 2026-07-17 #4: авто-переход single_choice/loader_processing — WEB-919:
  // обложка с ctaText) переехали в ОДНУ общую точку ./screenAdvance. Оттуда же
  // их берёт конструктор — пометка экрана в списке экранов и предупреждение
  // перед публикацией. Здесь была первая копия правила; вторую в конструкторе
  // заводить нельзя, они разъедутся. Сторож на расхождение —
  // QuizScreenWizard.advanceParity.web1704.test.tsx.
  const hasAdvanceMeans = screenHasAdvanceMeans(visibleBlocks);

  const isFullScreenBg =
    screenLayout.mode === 'full-screen' && screenLayout.hasBackgroundImage;
  // Подложка липкой CTA: поверх полноэкранной картинки непрозрачная плашка
  // цвета страницы закрыла бы фото — там кнопка лежит на самой картинке.
  // Значение одно и то же в стиле и в data-атрибуте, чтобы тест не мог
  // позеленеть на атрибуте при разъехавшемся стиле (jsdom теряет var() в
  // background-шорткате).
  const ctaSurface = isFullScreenBg ? 'transparent' : 'var(--color-bg-primary, transparent)';

  // WEB-1794: запасная кнопка на застрявшем авто-переходном экране.
  //
  // Обычная страховка (ниже) рисуется, когда способа продвинуться НЕТ вовсе.
  // Здесь способ формально есть — авто-переход, — но он уже пробовал и
  // упёрся: человек набрал текст и замер, не уводя фокус из поля (цена
  // честного правила WEB-1643, которое ждёт ЗАВЕРШЁННОГО ввода). Экран
  // стоит, а кнопки на нём нет по построению — тот самый тупик, за который
  // WEB-1107 был Urgent.
  //
  // Условий три, и каждое обязательное:
  //  - попытка авто-перехода на ЭТОМ экране была и не состоялась
  //    (признак застревания внутри useAutoAdvance, ставится по возврату
  //    handleNext);
  //  - авто-переход — ЕДИНСТВЕННЫЙ способ уйти (screenAdvanceIsAutoOnly):
  //    рядом с настоящей кнопкой или hero-CTA человеку есть куда нажать,
  //    вторая кнопка того же смысла только мешает;
  //  - истёк таймер ПОКАЗА (не перехода) — иначе кнопка мелькала бы при
  //    штатном заполнении, между последним символом и потерей фокуса.
  // Экраны с выбором варианта без обязательных соседей сюда не попадают
  // вовсе: там переход состоялся, застревания нет.
  const isRescue = rescueVisible && screenAdvanceIsAutoOnly(visibleBlocks);

  // Поведение CTA из стиль-пака: липкая снизу / в потоке / без кнопки
  // (авто-переход по клику — 12min против Asana Rebel и Babbel).
  // Без пака поведение прежнее: кнопка в потоке по центру.
  if (hasAdvanceMeans && !isRescue) return null;

  // WEB-1025: системная запасная кнопка «Далее» — не блок: её нет в списке
  // блоков экрана, её нельзя ни выделить, ни отредактировать, ни удалить.
  // Именно из-за неё заведена карточка.
  //
  // Решение владельца 2026-08-24 («Вариант А», после возврата со
  // скриншотами): в канве конструктора она не рисуется ВООБЩЕ — ни на
  // пустом экране, ни на экране с блоками. Прежнее условие
  // (`isEditorPreview && visibleBlocks.length === 0`) исполняло решение PM
  // 2026-08-04 буквально — «на НОВОМ экране не должно быть кнопок» — и
  // разваливалось от первого же блока: автор клал «Заголовок»,
  // visibleBlocks.length становился 1, и фантомная кнопка возвращалась.
  // Дословно владелец: «Всё, что есть на экране, должно быть редактируемо».
  // Настоящий блок «Кнопка Далее» вместо неё кладёт АВТОР руками из палитры
  // блоков (BlockPickerModal, раздел «Навигация») — он виден в списке блоков,
  // правится и удаляется. Автоматически его никто не подставляет: reducer
  // addScreen в store/editorSlice.ts создаёт экран с blocks: [] и ничего в
  // него не кладёт (проверено 25.08 — прежняя редакция этого комментария
  // утверждала обратное и была неправдой). Забытую кнопку ловит не подстановка,
  // а предупреждение: WEB-1704 помечает такой экран в списке экранов и держит
  // публикацию (components/editor/LayersPalettePanel.tsx +
  // confirmPublishEmptyScreens.ts).
  //
  // ⚠ Публичная страница НЕ ЗАТРОНУТА и трогать её нельзя: там страховка
  // остаётся всегда — и на боевом опубликованном показе, и в режиме
  // предпросмотра черновика (`?preview=1`, WEB-411). Без неё экран без
  // блока-кнопки — тупик: покупатель не доходит до оплаты (WEB-1107, был
  // Urgent; WEB-1118/L1 — тот же тупик вторым заходом). Признак «я в канве
  // конструктора» — только isEditorPreview (WEB-681/683): PhonePreview.tsx
  // оборачивает визард в EditorPreviewProvider, PublicQuiz.tsx — никогда,
  // ни в бою, ни в `?preview=1` (по isPreview это ломалось, часть 2).
  if (isEditorPreview) return null;

  const ctaPack = chromeExtras.pack;
  // Слово владельца 21.10.2026: положение — поле воронки settings.quizCta
  // сильнее пака (одна дверь resolveQuizCta, useQuizChromeExtras).
  const placement = chromeExtras.cta.placement;
  // WEB-1107 (Urgent, QA 05.08): placement:'none' раньше гасил запасную
  // кнопку безусловно — предполагалось, что «экран ведут сами варианты
  // ответа». Но до этой строки код доходит, только если hasAdvanceMeans
  // уже ложно (проверено выше — блок-кнопка, авто-переход и обложка с
  // ctaText разом) — то есть на экране НЕТ блока с реальным autoAdvance:true
  // (WEB-926/682). На «Чистом белом» и остальных placement:'none' паках
  // (warm-cream, cinematic-photo, editorial-serif, mono-glam) экран без
  // такого блока — например, только текст/картинка — оставался вовсе без
  // кнопки: покупатель застревал намертво. Раньше это не давало о себе
  // знать благодаря нередактируемой кнопке-заглушке, которую убрали в
  // WEB-1025 (WEB-1107 — вторая точка, где вскрылся тот же тупик).
  // Правило теперь одно: если пак не показывает свою CTA-обвязку И на
  // экране нет своего способа продолжить (button_next/autoAdvance/hero-cta,
  // всё уже отфильтровано строкой выше) — рисуем обычную кнопку в потоке,
  // не рискуя оставить экран без выхода.
  const sticky = placement === 'sticky-bottom';
  const fullWidth = ctaPack ? ctaPack.cta.fullWidth : false;
  const height = ctaPack?.cta.height;

  const fallbackNode = (
    <div
      data-testid="quiz-next-button-wrap"
      data-rescue={isRescue ? 'true' : undefined}
      data-placement={placement}
      data-cta-surface={ctaSurface}
      style={{
        textAlign: 'center',
        // Пачка настроек 2: в прибитой панели поле сверху не нужно.
        marginTop: chromeExtras.dock ? 0 : 24,
        ...(sticky
          ? {
              position: 'sticky' as const,
              bottom: 0,
              paddingTop: 12,
              paddingBottom: 12,
              // Подложка того же цвета, что страница: иначе контент
              // просвечивает под липкой кнопкой при скролле.
              // WEB-927: поверх полноэкранной картинки подложки нет —
              // непрозрачная плашка закрыла бы фото (ctaSurface).
              background: ctaSurface,
              zIndex: 2,
            }
          : {}),
      }}
    >
      <button
        onClick={onNext}
        // Ревизия пачки 8 (данные лида): нажатие не отбирает фокус у поля —
        // иначе `focusout` опережал клик, авто-переход срабатывал сам, а
        // палец приземлялся уже на следующем экране (keepFieldFocusOnPress).
        onMouseDown={keepFieldFocusOnPress}
        // WEB-1794: разные признаки у двух случаев — «на экране нечем
        // продвинуться» и «авто-переход упёрся». Внешне это ОДНА и та же
        // кнопка (общий код рендера ниже — расхождения в цвете и форме,
        // как в ревизии волны 25.08, здесь невозможны), но сторож паритета
        // WEB-1704 обязан считать только первый случай, иначе он позеленеет
        // на кнопке, к правилу «есть чем продвинуться» не относящейся.
        data-testid={
          isRescue ? 'quiz-next-button-rescue' : 'quiz-next-button-fallback'
        }
        // WEB-922 (возврат QA 2026-08-01): запасная кнопка тускнеет по тому
        // же признаку, что и блок «Кнопка Далее» — иначе на сгенерированных
        // экранах (их большинство) ось пака disabledStyle не видна вообще.
        // Кликабельность и warning WEB-895 не трогаем.
        className={dimmed ? 'funnel-next-unfilled' : undefined}
        style={{
          // Ревизия волны 25.08: цвет текста воронки — как у блочной кнопки
          // «Далее» и у остальных кнопок (CTA, кнопка-ссылка, обложка). Пока
          // блочную рисовал сам визард, обе звали сборщик без цвета; после
          // перевода блочной на общий рендер она цвет получила, а запасная
          // осталась — и две кнопки одного назначения на одном экране стали
          // выглядеть по-разному. Экранов с запасной кнопкой большинство
          // (сгенерированные), так что расхождение видел бы почти каждый.
          ...primaryButtonStyle(buttonStyle, textColor),
          ...(fullWidth || sticky || chromeExtras.dock ? { width: '100%' } : {}),
          ...(height ? { minHeight: `${height}px` } : {}),
          ...packNextButtonStyle,
        }}
      >
        {isLast
          ? getPublicQuizStrings(language).finishLabel
          : `${getPublicQuizStrings(language).continueLabel} →`}
      </button>
    </div>
  );
  // Пачка настроек 2: пак 'fixed-bottom' — запасная кнопка тоже прибита.
  return chromeExtras.dock ? (
    <chromeKit.CtaDock
      config={chromeExtras.dock}
      canScroll={chromeExtras.canScroll}
      preview={isEditorPreview}
    >
      {fallbackNode}
    </chromeKit.CtaDock>
  ) : (
    fallbackNode
  );
}
